/**
 * Customer 360 light helpers (Epic 2/3) — RFM bucket shared with /api/metrics.
 */

export type RfmBucket = "active" | "at_risk" | "hibernating";

export type CustomerCohortFilter =
  | "all"
  | "new"
  | "returning"
  | RfmBucket;

export const RFM_BUCKET_LABELS: Record<RfmBucket, string> = {
  active: "Activo",
  at_risk: "En riesgo",
  hibernating: "Hibernando",
};

export const CUSTOMER_COHORT_LABELS: Record<CustomerCohortFilter, string> = {
  all: "Todos",
  new: "Nuevos (1 pedido)",
  returning: "Recurrentes (2+)",
  active: "RFM activo",
  at_risk: "RFM en riesgo",
  hibernating: "RFM hibernando",
};

/** Same thresholds as api.metrics RFM: 0–30 active, 31–90 at_risk, >90 hibernating. */
export function classifyRfmBucket(
  lastOrderAt: Date | string | null | undefined,
  now = new Date(),
): RfmBucket {
  if (!lastOrderAt) return "hibernating";
  const d = typeof lastOrderAt === "string" ? new Date(lastOrderAt) : lastOrderAt;
  if (Number.isNaN(d.getTime())) return "hibernating";
  const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (days <= 30) return "active";
  if (days <= 90) return "at_risk";
  return "hibernating";
}

export function daysSince(
  lastOrderAt: Date | string | null | undefined,
  now = new Date(),
): number | null {
  if (!lastOrderAt) return null;
  const d = typeof lastOrderAt === "string" ? new Date(lastOrderAt) : lastOrderAt;
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / 86_400_000));
}

export type CohortCustomerRow = {
  orderCount: number;
  lastOrderAt: string | null | undefined;
};

/** Light list filters for /app/clientes (Epic 3). */
export function matchesCustomerCohort(
  row: CohortCustomerRow,
  cohort: CustomerCohortFilter,
  now = new Date(),
): boolean {
  if (cohort === "all") return true;
  if (cohort === "new") return row.orderCount <= 1;
  if (cohort === "returning") return row.orderCount >= 2;
  return classifyRfmBucket(row.lastOrderAt, now) === cohort;
}

export function parseCustomerCohort(raw: unknown): CustomerCohortFilter {
  const v = typeof raw === "string" ? raw : "all";
  const allowed: CustomerCohortFilter[] = [
    "all",
    "new",
    "returning",
    "active",
    "at_risk",
    "hibernating",
  ];
  return allowed.includes(v as CustomerCohortFilter) ? (v as CustomerCohortFilter) : "all";
}
